// Hero.js
import React, { useState, useEffect, useRef } from "react";
import "../../../assets/css/patient/Hero.css";

function useTypewriter(text, isActive, speed = 55) {
  const [displayed, setDisplayed] = useState("");
  const indexRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    setDisplayed("");
    indexRef.current = 0;
    clearInterval(timerRef.current);
    if (!isActive || !text) return;
    const startDelay = setTimeout(() => {
      timerRef.current = setInterval(() => {
        indexRef.current += 1;
        setDisplayed(text.slice(0, indexRef.current));
        if (indexRef.current >= text.length) clearInterval(timerRef.current);
      }, speed);
    }, 150);
    return () => { clearTimeout(startDelay); clearInterval(timerRef.current); };
  }, [text, isActive, speed]);

  return displayed;
}

const Slide = ({ slide, isActive, isExiting }) => {
  const typedTitle = useTypewriter(slide.title, isActive, 38);
  const cls = (base) => `${base} ${isActive ? "animate" : ""} ${isExiting ? "exit" : ""}`;

    useEffect(() => {
      window.scrollTo({ top: 0, behavior: "instant" });
    }, []);

  return (
    <div className="slide">
      <img src={slide.image} alt={slide.title} className="slide-image" loading="lazy" />
      <div className="slide-overlay" />
      <div className="slide-content">

        {/* Tag chip */}
        {slide.tag && (
          <span className={cls("slide-tag")}>{slide.tag}</span>
        )}

        {/* Title */}
        <h1 className={cls("slide-title")}>
          {isActive ? typedTitle : slide.title}
        </h1>

        {/* Divider */}
        <div className={cls("slide-divider")} />

        {/* Ayat + Translation */}
        {slide.ayat && (
          <>
            <div className={cls("ayat-arabic")}>{slide.ayat}</div>
            <div className={cls("ayat-translation")}>{slide.ayatTranslation}</div>
          </>
        )}

        {/* Description */}
        <p className={cls("slide-description")}>{slide.description}</p>

      </div>
    </div>
  );
};

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [exitingSlide, setExitingSlide] = useState(null);
  const timerRef = useRef(null);

  const slides = [
    {
      tag: "Mental Wellness",
      title: "We Understand Your Struggles",
      description:
        "Professional support for stress, anxiety, and depression. You're not alone in your journey to mental wellness.",
      image:
        "https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?w=1920&h=1080&fit=crop&q=80",
    },
    {
      tag: "AI Technology",
      title: "AI-Powered Mental Health Support",
      description:
        "Experience innovative AI technology that provides 24/7 support, personalized insights, and instant guidance for your mental wellness journey.",
      image:
        "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1920&h=1080&fit=crop&q=80",
    },
    {
      tag: "Spiritual Healing",
      title: "Find Peace Through Quranic Healing",
      description:
        "Combine therapy with Islamic spiritual healing through curated wazaif and Quranic guidance.",
      image:
        "https://images.unsplash.com/photo-1609599006353-e629aaabfeae?w=1920&h=1080&fit=crop&q=80",
      ayat: "فَإِنَّ مَعَ ٱلْعُسْرِ يُسْرًا",
      ayatTranslation: "Indeed, with hardship comes ease. — Surah Ash-Sharh 94:5",
    },
    {
      tag: "Expert Care",
      title: "Connect With Expert Therapists",
      description:
        "Access experienced mental health professionals for personalized therapy sessions tailored to your unique needs.",
      image:
        "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=1920&h=1080&fit=crop&q=80",
    },
  ];

  const goToSlide = (nextIndex) => {
    if (nextIndex === currentSlide) return;
    setExitingSlide(currentSlide);
    setTimeout(() => { setExitingSlide(null); setCurrentSlide(nextIndex); }, 380);
  };

  const startTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrentSlide((prev) => {
        const next = (prev + 1) % slides.length;
        setExitingSlide(prev);
        setTimeout(() => setExitingSlide(null), 380);
        return next;
      });
    }, 5000);
  };

  useEffect(() => { startTimer(); return () => clearInterval(timerRef.current); }, []); // eslint-disable-line

  const handleDotClick = (index) => {
    clearInterval(timerRef.current);
    goToSlide(index);
    setTimeout(() => startTimer(), 400);
  };

  return (
    <section className="hero-section">
      <div className="hero-container">
        <div className="slides-wrapper" style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
          {slides.map((slide, index) => (
            <Slide
              key={index}
              slide={slide}
              isActive={currentSlide === index}
              isExiting={exitingSlide === index}
            />
          ))}
        </div>
        <div className="dots-container">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`dot ${currentSlide === index ? "active" : ""}`}
              onClick={() => handleDotClick(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;